<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>

  @if (item) {
    <app-page-detail-hero [auditStamp]="item"
      icon="pi pi-box"
      [eyebrow]="'Warehousing Item ' + (item.itemNumber || 'AUTO')"
      [title]="item.itemName + (itemSizeLabel(item) ? ' | ' + itemSizeLabel(item) : '')"
      [subtitle]="clientName(item.crmClientId) + ' | ' + accountName(item.crmAccountId)">
      <div tags>
        <p-tag [value]="item.itemNumber || 'AUTO ITEM'" severity="info"></p-tag>
        @if (itemSizeLabel(item)) { <p-tag [value]="itemSizeLabel(item)" severity="secondary"></p-tag> }
        <p-tag [value]="item.trackingMode === 'UNIT' || item.isDisplayStand ? 'UNIT-LEVEL TRACKING REQUIRED' : item.trackingMode" [severity]="item.trackingMode === 'UNIT' || item.isDisplayStand ? 'warn' : 'secondary'"></p-tag>
        <p-tag [value]="item.isActive === false ? 'INACTIVE' : 'ACTIVE'" [severity]="statusSeverity(item.isActive === false ? 'INACTIVE' : 'ACTIVE')"></p-tag>
      </div>
      <div actions>
        <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/warehousing/items"></p-button>
        <p-button label="View Transactions" icon="pi pi-history" severity="secondary" [outlined]="true"
          [routerLink]="['/warehousing/stock-ledger']" [queryParams]="{ itemId: item.id }"></p-button>
        @if (canEdit) {
          @if (item.trackingMode === 'UNIT') {
            <p-button label="Repair Pooled Units" icon="pi pi-wrench" severity="warn" [outlined]="true" [loading]="trackingBusy" (click)="previewTrackingRepair()"></p-button>
          }
          <p-button label="Change Tracking" icon="pi pi-shield" severity="secondary" [outlined]="true" [loading]="trackingBusy" (click)="previewTrackingChange()"></p-button>
          <p-button label="Edit" icon="pi pi-pencil" severity="info" (click)="editItem()"></p-button>
        }
        <p-button label="Create" icon="pi pi-plus" routerLink="/warehousing/items/add"></p-button>
      </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <div class="warehousing-detail-grid">
      <div class="warehousing-detail-stack">
        <app-page-form-section icon="pi pi-building" title="Ownership" subtitle="Client and customer item identifiers.">
          <div class="warehousing-detail-list">
            <div><span>Client</span><strong>{{ clientName(item.crmClientId) }}</strong></div>
            <div><span>Account</span><strong>{{ accountName(item.crmAccountId) }}</strong></div>
            <div><span>Customer Item #</span><strong>{{ item.customerItemNumber || '-' }}</strong></div>
            <div><span>Barcode</span><strong>{{ item.itemBarcode || '-' }}</strong></div>
            <div><span>SKU</span><strong>{{ item.skuNumber || '-' }}</strong></div>
            <div><span>Tenant UOM</span><strong>{{ item.tenantUomName || masterName('TENANT_UOM', item.tenantUomId) }}</strong></div>
          </div>
        </app-page-form-section>

        <app-page-form-section icon="pi pi-sitemap" title="Classification" subtitle="Catalog hierarchy used for search and billing context.">
          <div class="warehousing-detail-list">
            <div><span>Hierarchy</span><strong>{{ hierarchyLabel() }}</strong></div>
            <div><span>Category</span><strong>{{ item.categoryName || masterName('CATEGORY', item.categoryId) }}</strong></div>
            <div><span>Brand</span><strong>{{ item.brandName || masterName('BRAND', item.brandId) }}</strong></div>
            <div><span>Sub Brand</span><strong>{{ item.subBrandName || masterName('SUB_BRAND', item.subBrandId) }}</strong></div>
          </div>
        </app-page-form-section>
      </div>

      <div class="warehousing-detail-stack">
        <app-page-form-section icon="pi pi-expand" title="Dimensions" subtitle="Storage footprint and handling measurements.">
          <div class="warehousing-detail-list">
            <div><span>Unit</span><strong>{{ dimensionUnitName() }}</strong></div>
            <div><span>Length</span><strong>{{ item.lengthMeters | number:'1.0-4' }} {{ dimensionUnitName() }}</strong></div>
            <div><span>Breadth</span><strong>{{ item.breadthMeters | number:'1.0-4' }} {{ dimensionUnitName() }}</strong></div>
            <div><span>Height</span><strong>{{ item.heightMeters | number:'1.0-4' }} {{ dimensionUnitName() }}</strong></div>
            <div><span>Weight</span><strong>{{ item.weightKg || 0 | number:'1.2-4' }} kg</strong></div>
            <div><span>SQM</span><strong>{{ item.sqm | number:'1.2-4' }}</strong></div>
            <div><span>CBM</span><strong>{{ item.cbm | number:'1.2-4' }}</strong></div>
          </div>
        </app-page-form-section>

        <app-page-form-section icon="pi pi-align-left" title="Description" subtitle="Operational description and item notes.">
          @if (item.itemDescription) {
            <div class="warehousing-detail-note" [innerHTML]="item.itemDescription"></div>
          } @else {
            <p class="warehousing-detail-note">No description provided.</p>
          }
        </app-page-form-section>

        <app-page-form-section icon="pi pi-images" title="Images" subtitle="Uploaded item references.">
          @if (item.images?.length) {
            <div class="warehousing-media-grid">
              <p-image *ngFor="let image of item.images" [src]="image.fileUrl" [alt]="image.fileName || item.itemName + (itemSizeLabel(item) ? ' ' + itemSizeLabel(item) : '')" width="160" height="110" [preview]="true"></p-image>
            </div>
          } @else {
            <div class="warehousing-empty-state"><i class="pi pi-images"></i><strong>No item images</strong><span>No item images uploaded.</span></div>
          }
        </app-page-form-section>
      </div>
    </div>
  } @else if (!isLoading) {
    <div class="warehousing-empty-detail">
      <i class="pi pi-search"></i>
      <strong>Item not found</strong>
      <span>The selected warehousing item could not be loaded.</span>
    </div>
  }
</app-page-shell>

<p-dialog [header]="trackingRepair ? 'Repair pooled unit stock' : 'Change item tracking'" [(visible)]="trackingDialogVisible" [modal]="true" [draggable]="false"
  [style]="{ width: 'min(42rem, 95vw)' }" [breakpoints]="{ '640px': '95vw' }">
  @if (trackingPreview && !trackingResult) {
    <div class="surface-50 border-round p-3 mb-3">
      <strong>{{ trackingPreview.fromMode }} → {{ trackingPreview.toMode }}</strong>
      <p class="mb-0 text-600">{{ trackingPreview.pools.length }} stock pools need splitting. Existing receipts and totals stay intact.</p>
      @if (trackingRepair && !trackingPreview.pools.length) { <small>No pooled stock needs repair for this item.</small> }
    </div>
    @if (trackingPreview.blockers.length) {
      <div class="surface-100 border-round p-3 mb-3" role="alert">
        <strong>Reconciliation required</strong>
        @for (blocker of trackingPreview.blockers; track $index) {
          <div>Stock unit {{ blocker.stockUnitId || '—' }}: {{ blocker.reasons?.join(', ') || blocker.reason }} {{ blocker.requiredAdjustment }}</div>
        }
        <small>The request can be recorded as pending. Tracking stays {{ trackingPreview.fromMode }} until stock is reconciled and a fresh preview is confirmed.</small>
      </div>
    }
    <p-inputGroup class="w-full"><p-inputGroupAddon><i class="pi pi-pencil"></i></p-inputGroupAddon>
      <p-iftalabel><textarea pTextarea id="trackingReason" [(ngModel)]="trackingReason" rows="3" class="w-full"></textarea>
        <label for="trackingReason">Reason for tracking change *</label></p-iftalabel></p-inputGroup>
    <div class="flex justify-content-end gap-2 mt-3">
      <p-button label="Cancel" severity="secondary" [outlined]="true" (click)="trackingDialogVisible = false"></p-button>
      @if (trackingPreview.pools.length || !trackingRepair) {
        <p-button [label]="trackingPreview.canConfirm ? (trackingRepair ? 'Confirm Repair' : 'Confirm Tracking Change') : 'Record Pending Request'"
          [loading]="trackingBusy" [disabled]="!trackingReason.trim()" (click)="confirmTrackingChange()"></p-button>
      }
    </div>
  }
  @if (trackingResult) {
    <p><strong>{{ trackingResult.status === 'COMPLETED' ? (trackingRepair ? 'Stock repaired' : 'Tracking changed') : 'Pending reconciliation' }}</strong></p>
    <p>Operational mode: {{ trackingResult.operationalMode }}</p>
    @if (trackingResult.manifest?.length) {
      <p>Print labels for the new unit codes before handling these pieces:</p>
      <div class="overflow-auto" style="max-height: 16rem">
        @for (piece of trackingResult.manifest; track piece.stockUnitId) {
          @if (piece.unitCode !== piece.oldCode) {
            <div>{{ piece.unitCode }} — source {{ piece.oldCode }} (piece {{ piece.piece }})</div>
          }
        }
      </div>
    }
    <div class="flex justify-content-end mt-3"><p-button label="Close" (click)="trackingDialogVisible = false"></p-button></div>
  }
  @if (trackingError) { <p class="text-red-600" role="alert">{{ trackingError }}</p> }
</p-dialog>
